/**
 * The frame every page sits in: the navigation bar, a main region, and the
 * footer.
 *
 * The navigation is deliberately short — Connect, Account, and Admin only for
 * an admin — because the product's surface is the extension and the assistant;
 * this site exists to link a browser, hand over one URL, and show what has
 * been done with it.
 */

import type { ReactNode } from "react";
import Container from "react-bootstrap/Container";
import Nav from "react-bootstrap/Nav";
import Navbar from "react-bootstrap/Navbar";
import Button from "react-bootstrap/Button";
import Spinner from "react-bootstrap/Spinner";
import { Link, NavLink, Outlet, useLocation } from "react-router";
import { DOCS_URL, REPO_URL } from "../links";
import { signInHref, useSession } from "../useSession";
import { BrandMark, LinkButton } from "./ui";
import { ThemeToggle } from "./theme";

function AccountControl(): ReactNode {
  const { account, loading, signOut } = useSession();
  const location = useLocation();

  if (loading) {
    return <Spinner animation="border" size="sm" role="status" aria-label="Checking your session" className="mx-2 text-body-secondary" />;
  }
  if (!account) {
    return (
      <LinkButton to={signInHref(`${location.pathname}${location.search}`)} size="sm">
        Sign in
      </LinkButton>
    );
  }
  return (
    <div className="d-flex align-items-center gap-2">
      <span className="emb-muted small d-none d-lg-inline">{account.username}</span>
      <Button size="sm" variant="outline-secondary" onClick={() => void signOut()}>
        Sign out
      </Button>
    </div>
  );
}

export function Layout(): ReactNode {
  const { account } = useSession();

  return (
    <>
      <Navbar expand="md" sticky="top" className="emb-navbar py-2">
        <Container>
          <Navbar.Brand as={Link} to="/" className="emb-brand">
            <BrandMark />
            <span>
              xtract.bot <span className="fw-normal emb-muted">Embellisher</span>
            </span>
          </Navbar.Brand>
          <Navbar.Toggle aria-controls="emb-nav" />
          <Navbar.Collapse id="emb-nav">
            <Nav className="me-auto">
              <Nav.Link as={NavLink} to="/install">
                Install
              </Nav.Link>
              <Nav.Link as={NavLink} to="/connect">
                Connect
              </Nav.Link>
              <Nav.Link as={NavLink} to="/account">
                Account
              </Nav.Link>
              {account?.isAdmin ? (
                <Nav.Link as={NavLink} to="/admin">
                  Admin
                </Nav.Link>
              ) : null}
            </Nav>
            <div className="d-flex align-items-center gap-2 mt-3 mt-md-0">
              <ThemeToggle />
              <AccountControl />
            </div>
          </Navbar.Collapse>
        </Container>
      </Navbar>

      <main className="emb-main">
        <Outlet />
      </main>

      <footer className="emb-footer">
        <Container className="d-flex flex-column flex-md-row gap-3 justify-content-between align-items-md-center">
          <div className="d-flex align-items-center gap-2 emb-muted">
            <BrandMark size={20} />
            <span>xtract.bot Embellisher</span>
          </div>
          <nav className="d-flex flex-wrap gap-3" aria-label="Footer">
            <Link to="/install">Install the extension</Link>
            <Link to="/connect">Connect an assistant</Link>
            <Link to="/account">Your account</Link>
            <a href={DOCS_URL} target="_blank" rel="noreferrer noopener">
              Documentation
            </a>
            <a href={REPO_URL} target="_blank" rel="noreferrer noopener">
              Source
            </a>
          </nav>
          <p className="emb-muted mb-0 small">
            Your captured data never leaves your browser — capture logs live in the extension, not here.
          </p>
        </Container>
      </footer>
    </>
  );
}

/** The frame without the navigation: the consent and link pages, which are one decision each. */
export function BareLayout(): ReactNode {
  return (
    <>
      <div className="emb-navbar py-3">
        <Container className="d-flex align-items-center justify-content-between">
          <span className="emb-brand">
            <BrandMark />
            <span>
              xtract.bot <span className="fw-normal emb-muted">Embellisher</span>
            </span>
          </span>
          <ThemeToggle />
        </Container>
      </div>
      <main className="emb-main">
        <Outlet />
      </main>
    </>
  );
}
